/**
 * Configuration Types
 *
 * Type definitions for all configuration options available in the chat widget.
 * Based on V1 configuration structure.
 */

import type { Rules } from '@/configuration/translateRules';
import type { Border, PrivacyDisclaimer } from '@/domain/configurationShared';
import type { V2Configuration } from '@/domain/configurationV2';
import type { DeviceConfig } from '@/domain/deviceConfig';

export type { Border, PrivacyDisclaimer } from '@/domain/configurationShared';

export interface ConfigState {
  device: DeviceConfig;
  dynamic: DynamicConfiguration;
  server: ServerConfiguration;
}

export interface WidgetConfiguration {
  // Styling & Appearance (legacy/flat structure - for backward compatibility)
  border: Border;
  borderRadius: BorderRadius;
  colors: Record<string, string>; // Custom color overrides (legacy)
  zIndex: number;
  alignment: Alignment;
  // font: string; // Legacy font (maps to theme.fontLibrary.primary)

  // Theme (nested structure - preferred)
  theme?: Theme;

  // Button Configuration
  initialChatButton: InitialChatButton;
  minimizedChatButton: MinimizedChatButton;
  closeButton: CloseButton;

  // Display Rules
  display: DisplayType;
  displayRules: DisplayRules;
  // alwaysShow: boolean;

  // Onboarding
  onboarding: OnboardingConfig;

  // Chat Messages
  ongoingChat: OngoingChatConfig;
  chatClosed: ChatMessageConfig;
  chatThrottled: ChatMessageConfig;

  // Features
  attachmentsEnabled: boolean;
  widgetType: WidgetType;
  selfService: SelfServiceConfig;
  throttling: ThrottlingConfig;

  // Custom Text
  customText?: {
    header?: {
      greeting?: string;
      title?: string;
    };
    interactiveOnboarding?: {
      emailPrompt?: string;
    };
  };

  // Other
  lng: string; // Language code (e.g., 'en-us')
  proactiveChat?: ProactiveChatConfig;
  showPoweredByGladly?: boolean;
  privacyDisclaimer?: PrivacyDisclaimer;
  analyticsAttribution?: AnalyticsAttribution;
  audienceId?: string;

  // Provider Configuration
  provider?: string;
  providerConfig?: Record<string, unknown>;

  // Rules (legacy format, translated to displayRules)
  rules?: Rules;

  // V2 (present when V2 widget is loaded)
  v2: V2Configuration;
  v2Enabled: boolean;
}

// ============================================================================
// Enums
// ============================================================================

export const ALIGNMENTS = {
  LEFT: 'LEFT',
  RIGHT: 'RIGHT',
} as const;

export type Alignment = (typeof ALIGNMENTS)[keyof typeof ALIGNMENTS];

export const AVAILABILITY = {
  AVAILABLE: 'AVAILABLE',
  BUSINESS_CLOSED: 'BUSINESS_CLOSED',
  PERCENT_THROTTLED: 'PERCENT_THROTTLED',
  THROTTLED: 'THROTTLED',
} as const;

export type Availability = (typeof AVAILABILITY)[keyof typeof AVAILABILITY];

export const BORDER_RADII = {
  NONE: 'NONE',
  SMALL: 'SMALL',
  LARGE: 'LARGE',
} as const;

export type BorderRadius = (typeof BORDER_RADII)[keyof typeof BORDER_RADII];

export const CLOSE_ICON_TYPE = {
  DASH_ICON: 'DASH_ICON',
  X_ICON: 'X_ICON',
} as const;

export type CloseIconType = (typeof CLOSE_ICON_TYPE)[keyof typeof CLOSE_ICON_TYPE];

export const DISPLAY_TYPE = {
  ALL_PAGES: 'ALL_PAGES',
  RULES_ONLY: 'RULES_ONLY',
  DISABLED: 'DISABLED',
} as const;

export type DisplayType = (typeof DISPLAY_TYPE)[keyof typeof DISPLAY_TYPE];

export const INITIAL_BUTTON_TYPES = {
  TEXT: 'TEXT',
  ICON: 'ICON',
} as const;

export type InitialButtonType = (typeof INITIAL_BUTTON_TYPES)[keyof typeof INITIAL_BUTTON_TYPES];

/**
 * Which chat entry point the minimized widget shows. BAR is the default Enhanced
 * behavior: the chat bar, which a page rule or a dismissal can still turn into the
 * button. BUTTON shows the button on every page. The Legacy UI has no bar, so it
 * always shows the button.
 */
export const LAUNCHER_DISPLAY = {
  BAR: 'BAR',
  BUTTON: 'BUTTON',
} as const;

export type LauncherDisplay = (typeof LAUNCHER_DISPLAY)[keyof typeof LAUNCHER_DISPLAY];

export const WIDGET_TYPES = {
  CHAT: 'CHAT',
  SELF_SERVICE: 'SELF_SERVICE',
} as const;

export type WidgetType = (typeof WIDGET_TYPES)[keyof typeof WIDGET_TYPES];

export const DISPLAY_RULE_TYPE = {
  URL: 'URL',
} as const;

export type DisplayRuleType = (typeof DISPLAY_RULE_TYPE)[keyof typeof DISPLAY_RULE_TYPE];

export const RULE_TYPES = {
  EXACT: 'EXACT',
  CONTAINS: 'CONTAINS',
  REGEX: 'REGEX',
} as const;

export type RuleType = (typeof RULE_TYPES)[keyof typeof RULE_TYPES];

// ============================================================================
// Widget Configuration (Main Configuration Object)
// ============================================================================

// Forward declarations for sub-types (defined below)
export interface CloseButton {
  iconType: CloseIconType;
  title: string;
}

export interface InitialChatButton {
  type: InitialButtonType;
  value: string;
}

export interface MinimizedChatButton {
  title: string;
}

export interface ChatMessageConfig {
  message: string;
  title: string;
}

export interface DisplayRule {
  type: string;
  id: string;
  displayDelay: number;
  content: {
    matchType: string;
    value: string;
    /** What a matching page shows instead of the polarity. See DISPLAY_RULE_BUTTON. */
    display?: string;
  };
}

export interface DisplayRules {
  showByDefault: boolean;
  rules: DisplayRule[];
}

export interface OnboardingConfig {
  enabled: boolean;
  header: string;
  introMessage: string;
  emailField: string;
  nameField: string;
  submitButton: string;
  authenticationError: string;
  blankFieldError: string;
  invalidEmailError: string;
  initialCompositionStage: string;
}

export interface OngoingChatConfig {
  title: string;
  compositionPlaceholder: string;
  submitButtonTitle: string;
  welcomeMessage: string;
  endChatTitle: string;
  endMessageAgentEnded: string;
  endMessageTimeExpired: string;
  endMessageConsumerNoReply: string;
  endMessageNewSessionStarted: string;
}

// Untyped: self-service is not implemented in V2. Kept to accept server config without validation errors.
export interface SelfServiceConfig {
  quickActions: unknown[];
}

export interface ThrottlingConfig {
  percentageRollout: number;
  autoThrottlingEnabled: boolean;
  autoThrottlingThreshold: number; // seconds
}

// Untyped: campaigns are not implemented in V2. Kept to accept server config without validation errors.
export interface ProactiveChatCampaign {
  [key: string]: unknown;
}

export interface ProactiveChatConfig {
  campaigns: ProactiveChatCampaign[];
}

export interface GAConfiguration {
  autoDetectTrackingId?: boolean;
  enabled?: boolean;
  gaTrackingId?: string;
}

export interface AnalyticsAttribution {
  gaConfiguration?: GAConfiguration;
}

export interface ThemeColors {
  alert?: string; // Alert icon color
  attachmentBackground?: string;
  brandAccent?: string; // Minimized chat icon bg color
  brandPrimary?: string; // Agent chat bubble color
  brandSecondary?: string; // Customer chat bubble color
  chatLauncher?: string; // Applies to the button color - let's use this PRIMARY
  greetingCloseButton?: string;
  productImageBackgroundColor?: string; // Background color for product images
  submitArrow?: string;
}

export interface FontLibrary {
  primary: string;
  secondary: string;
}

export interface ThemeFormats {
  time?: string; // e.g., 'h:mm a'
  date?: string | null; // e.g., 'MM/DD/YYYY' or null
}

export interface Theme {
  borderRadius?: BorderRadius;
  border?: Border;
  colors?: ThemeColors;
  dropshadowEnabled?: boolean;
  fontLibrary?: FontLibrary;
  formats?: ThemeFormats;
}

// ============================================================================
// Dynamic Configuration
// ============================================================================

export interface FeatureFlags {
  [key: string]: boolean;
}

export interface DynamicConfiguration {
  enabled: boolean; // Kill switch
  availability: Availability;
  percentageRollout: number; // 0-100
  featureFlags?: FeatureFlags;
}

// ============================================================================
// Init Configuration (passed to Gladly.init())
// ============================================================================

/**
 * What a host page may override at `Gladly.init()`, so a third-party A/B
 * framework can serve two variants from one embed and one stored configuration.
 *
 * This type is the whole overridable surface. The widget reads these keys and
 * nothing else, so a key absent here has no effect at runtime. The widget reads
 * it in configuration/applyConfigurationOverride.ts.
 */
export interface ConfigurationOverride {
  v2?: {
    /** Show the chat bar or the button. See LAUNCHER_DISPLAY. */
    display?: LauncherDisplay;
    /** Render the widget with the legacy (V1) look instead of the V2 look. */
    legacyUI?: boolean;
  };
}

export interface InitConfiguration {
  appId: string; // Required
  autoShowButton?: boolean;
  autoShowWidget?: boolean; // Legacy alias for autoShowButton
  cluster?: 'production' | 'staging';
  clusterUrl?: string; // Override clusterUrl from server config (local dev)
  configurationOverride?: ConfigurationOverride;
  environment?: 'production' | 'staging';
  gladlyCdn?: string; // Custom CDN URL
  widgetType?: WidgetType;
}

// ============================================================================
// Server Configuration (loaded from CDN)
// ============================================================================

export interface ServerConfiguration extends WidgetConfiguration {
  clusterUrl: string;
  gladlySite: string;
  gladlyStage?: string;
  loginDomain: string;
  orgId: string;
  providerId: string;
}

// ============================================================================
// Configuration State
// ============================================================================

export interface ConfigurationState {
  cohortId: string | null;
  configurationOverride: Partial<WidgetConfiguration>;
  config: WidgetConfiguration;
  dynamicConfiguration: DynamicConfiguration;
  dynamicConfigurationLastFetchedAt: string;
  serverConfiguration?: ServerConfiguration;
}

// actionColor
// chatOpenerBackgroundColor
// inputFontColor
// linkColor
export interface ThemeConfiguration {
  borderRadius: BorderRadius;
  border: Border;
  colors: ThemeColors;
  dropshadowEnabled: boolean;
  fontLibrary: FontLibrary;
  formats: ThemeFormats;
}
